import { AutoAwesome } from '@mui/icons-material';
import { Box, Button, Stack, Tooltip, Typography } from '@mui/material';
import { Info } from 'lucide-react';
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { UserCustomization } from '~/backend/types';
import AvailableModels from '~/components/ModelSelection/AvailableModels';
import CustomizationSelection from '~/components/ModelSelection/CustomizationSelection';
import PseudonymizeCheckbox from '~/components/ModelSelection/PseudonymizeCheckbox';
import { Modality } from '~/domain/dicom/modality';
import { PredictionModel } from '~/utils/transforms/predictionModelsDataTransforms';

type ImageTranslationModelSelectionProps = {
    modelCustomizations?: UserCustomization<unknown>[];
    availableModels?: PredictionModel[];
    modality?: Modality;
    disabled?: boolean;
    isLoadingModels?: boolean;
    isSubmitting?: boolean;
    showPseudonymization: boolean;
    onSubmit: (model: PredictionModel, selectedCustomization?: string) => void;
};

export default function ImageTranslationModelSelection({
    availableModels = [],
    modelCustomizations = [],
    modality,
    disabled = false,
    isLoadingModels,
    isSubmitting,
    showPseudonymization,
    onSubmit,
}: ImageTranslationModelSelectionProps) {
    const intl = useIntl();
    const [selectedModel, setSelectedModel] = useState<PredictionModel>();
    const [selectedCustomization, setSelectedCustomization] =
        useState<string>();

    const compatibleModels = useMemo(() => {
        if (!modality) {
            return availableModels;
        }
        return availableModels.filter(
            (model) =>
                model.inputModalities.length === 0 ||
                model.inputModalities.includes(modality)
        );
    }, [availableModels, modality]);

    const hiddenIncompatibleCount =
        availableModels.length - compatibleModels.length;
    const hasIncompatibleModels = hiddenIncompatibleCount > 0;
    const showHiddenNotice =
        hasIncompatibleModels && compatibleModels.length > 0 && !!modality;

    const customizations = useMemo(() => {
        if (!selectedModel || !modelCustomizations) {
            return [];
        }
        const selectedModelCustomizations =
            selectedModel?.customizations?.filter(
                (customization) => customization.name !== selectedModel.name
            );
        return selectedModelCustomizations || [];
    }, [selectedModel, modelCustomizations]);

    const hasCustomizations = customizations.length > 0;

    const hiddenModelsTitle = intl.formatMessage(
        { id: 'someModelsHiddenForModality' },
        { count: hiddenIncompatibleCount }
    );
    const hiddenModelsReason = intl.formatMessage(
        { id: 'someModelsHiddenForModality.reason' },
        { count: hiddenIncompatibleCount, modality }
    );
    const hiddenModelsAriaLabel = `${hiddenModelsTitle}. ${hiddenModelsReason}`;
    const hiddenModelsTooltip = (
        <Box>
            <Typography fontSize={12}>{hiddenModelsTitle}</Typography>
            <Typography fontSize={11} color="text.secondary">
                {hiddenModelsReason}
            </Typography>
        </Box>
    );

    return (
        <>
            <Box
                display="grid"
                gridTemplateRows="240px auto"
                gridTemplateColumns={hasCustomizations ? '60% 1fr' : '100%'}
                gap={hasCustomizations ? 1 : 0}
            >
                <AvailableModels
                    isLoading={isLoadingModels}
                    models={compatibleModels}
                    selectedModel={selectedModel}
                    onSelectedModelChanged={setSelectedModel}
                    emptyMessage={
                        hasIncompatibleModels && modality
                            ? intl.formatMessage(
                                  {
                                      id: 'noCompatibleModelsForModality',
                                  },
                                  { modality }
                              )
                            : undefined
                    }
                    labelAdornment={
                        showHiddenNotice ? (
                            <Tooltip title={hiddenModelsTooltip}>
                                <Box
                                    component="span"
                                    tabIndex={0}
                                    role="img"
                                    aria-label={hiddenModelsAriaLabel}
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Info size={12} aria-hidden />
                                </Box>
                            </Tooltip>
                        ) : undefined
                    }
                />

                {hasCustomizations && (
                    <CustomizationSelection
                        customizations={customizations}
                        selectedCustomization={selectedCustomization}
                        onSelectedCustomizationChanged={
                            setSelectedCustomization
                        }
                    />
                )}
            </Box>
            <Box display="flex" justifyContent="flex-end">
                <Stack gap={0.5}>
                    {showPseudonymization && <PseudonymizeCheckbox />}
                    <Tooltip
                        title={
                            !selectedModel ? (
                                <FormattedMessage id="selectAModelToStart" />
                            ) : (
                                ''
                            )
                        }
                        placement="top"
                    >
                        {/* Wrapper needed for MUI Tooltip to work on disabled buttons */}
                        <Box component="span">
                            <Button
                                onClick={() =>
                                    selectedModel &&
                                    onSubmit(
                                        selectedModel,
                                        selectedCustomization
                                    )
                                }
                                variant="contained"
                                endIcon={<AutoAwesome />}
                                disabled={
                                    !selectedModel || disabled || isSubmitting
                                }
                                sx={{ width: 160 }}
                            >
                                <FormattedMessage id="start" />
                            </Button>
                        </Box>
                    </Tooltip>
                </Stack>
            </Box>
        </>
    );
}
